import React from "react";
import { Link } from "react-router-dom";
import FadeIn from "../components/FadeIn";
import { IMAGES, DESTINATIONS } from "../lib/content";

// Warm, cinematic Kyoto sunset — sits firmly in the luxury editorial register.
// If / when the client supplies approved MP4 clips, this is swapped for
// <video> in the same slot (see components/HeroMontage.jsx scaffold).
const HERO_IMAGE =
  "https://customer-assets.emergentagent.com/job_luxury-journeys-jp/artifacts/8md296cu_AdobeStock_318440268.jpeg.webp";

const Home = () => {
  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative h-[100svh] min-h-[640px] w-full overflow-hidden" data-testid="hero">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Kyoto at the quiet hour"
            className="w-full h-full object-cover animate-kenburns"
            loading="eager"
            style={{ filter: "saturate(1.08) contrast(0.97) brightness(1.04)" }}
          />
          {/* Warmth wash — champagne radial */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse at 70% 35%, rgba(197,160,89,0.14) 0%, rgba(197,160,89,0) 55%)",
            }}
          />
          {/* Readability overlay — bottom weighted so imagery stays bright up top */}
          <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(5,5,5,0.2)_0%,rgba(5,5,5,0.1)_40%,rgba(5,5,5,0.78)_100%)]" />
          <div className="absolute inset-0 grain pointer-events-none" />
        </div>

        <div className="relative z-10 h-full container-editorial flex flex-col justify-end pb-24 md:pb-32">
          <div className="max-w-3xl">
            <FadeIn>
              <div className="overline mb-8" data-testid="hero-overline">Mensorio — Private Japan</div>
            </FadeIn>
            <FadeIn delay={150}>
              <h1 className="font-serif text-5xl md:text-7xl lg:text-[88px] leading-[1.02] text-bone tracking-tight drop-shadow-[0_2px_24px_rgba(0,0,0,0.5)]" data-testid="hero-headline">
                Private Japan,<br />
                <span className="italic text-bone/95">done properly.</span>
              </h1>
            </FadeIn>
            <FadeIn delay={300}>
              <div className="mt-10 max-w-xl text-bone/85 text-base md:text-lg font-light leading-relaxed space-y-2 drop-shadow-[0_2px_18px_rgba(0,0,0,0.55)]" data-testid="hero-sub">
                <p>Japan, thoughtfully experienced.</p>
                <p>Quietly exceptional. Carefully considered.</p>
                <p>Designed entirely around you.</p>
              </div>
            </FadeIn>
            <FadeIn delay={450}>
              <div className="mt-12">
                <Link
                  to="/plan"
                  className="inline-flex items-center gap-4 bg-gold text-ink px-10 py-5 text-[12px] tracking-[0.28em] uppercase font-medium hover:bg-gold-light transition-colors duration-500 shadow-[0_20px_60px_rgba(0,0,0,0.45)]"
                  data-testid="hero-cta"
                >
                  Plan Your Journey
                  <span className="w-8 h-px bg-ink/60" />
                </Link>
              </div>
            </FadeIn>
          </div>
        </div>

        <FadeIn delay={700}>
          <div className="absolute bottom-8 right-6 md:right-12 text-bone/60 text-[10px] tracking-[0.3em] uppercase" data-testid="hero-scroll-hint">
            Scroll — Slowly
          </div>
        </FadeIn>
      </section>

      {/* TRUST */}
      <section className="relative py-32 md:py-44 border-t border-white/5" data-testid="trust-section">
        <div className="container-editorial grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
          <div className="md:col-span-4">
            <FadeIn>
              <div className="overline">Section I</div>
              <div className="mt-4 font-serif text-2xl text-bone/80">Trust, quietly earned.</div>
            </FadeIn>
          </div>
          <div className="md:col-span-8">
            <FadeIn delay={120}>
              <h2 className="font-serif text-4xl md:text-6xl leading-[1.08] text-bone tracking-tight">
                Trusted by global clients.<br />
                <span className="text-bone/60">Designed with discretion.</span><br />
                <span className="italic text-gold">Built through international experience.</span>
              </h2>
            </FadeIn>
            <FadeIn delay={260}>
              <div className="mt-12 rule" />
              <p className="mt-8 max-w-xl text-bone/60 font-light leading-relaxed">
                We work with a small number of travelers each month. That constraint is not a posture — it is how quality is protected.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* DESTINATIONS — asymmetric bento */}
      <section className="relative py-32 md:py-44 border-t border-white/5" data-testid="destinations-section">
        <div className="container-editorial">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-20">
            <FadeIn>
              <div>
                <div className="overline">Section II — Destinations</div>
                <h2 className="mt-5 font-serif text-5xl md:text-6xl text-bone tracking-tight">Places, selected.</h2>
              </div>
            </FadeIn>
            <FadeIn delay={120}>
              <Link to="/destinations" className="link-underline text-[12px] tracking-[0.22em] uppercase text-bone/70 hover:text-bone" data-testid="view-all-destinations">
                View all destinations →
              </Link>
            </FadeIn>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10">
            {DESTINATIONS.map((d, i) => {
              const colSpans = ["md:col-span-7", "md:col-span-5", "md:col-span-5", "md:col-span-7", "md:col-span-12"];
              const heights = ["h-[420px] md:h-[560px]", "h-[420px] md:h-[560px]", "h-[380px] md:h-[460px]", "h-[380px] md:h-[460px]", "h-[360px] md:h-[520px]"];
              return (
                <FadeIn key={d.slug} delay={i * 80} className={`${colSpans[i]} group`}>
                  <article className={`relative overflow-hidden ${heights[i]}`} data-testid={`destination-card-${d.slug}`}>
                    <img
                      src={IMAGES.destinations[d.image]}
                      alt={d.name}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[2000ms] ease-out group-hover:scale-[1.06]"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                    <div className="absolute inset-0 p-8 md:p-10 flex flex-col justify-end">
                      <div className="overline text-[10px] mb-4">{d.tag}</div>
                      <h3 className="font-serif text-4xl md:text-5xl text-bone tracking-tight">{d.name}</h3>
                      <p className="mt-3 text-bone/75 font-light max-w-md">{d.short}</p>
                    </div>
                  </article>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* EXPERIENCES */}
      <section className="relative py-32 md:py-44 border-t border-white/5" data-testid="experiences-section">
        <div className="container-editorial grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          <div className="md:col-span-5">
            <FadeIn>
              <div className="overline">Section III — Experiences</div>
              <h2 className="mt-5 font-serif text-5xl md:text-6xl text-bone tracking-tight leading-[1.05]">
                Seamless.<br /><span className="italic text-gold">Elevated.</span>
              </h2>
              <p className="mt-8 max-w-md text-bone/60 font-light leading-relaxed">
                Every detail arranged in advance so the days feel unhurried — and the right moments arrive at the right time.
              </p>
              <Link to="/experiences" className="mt-10 inline-flex link-underline text-[12px] tracking-[0.22em] uppercase text-bone/70 hover:text-bone" data-testid="view-all-experiences">
                Explore experiences →
              </Link>
            </FadeIn>
          </div>

          <div className="md:col-span-7">
            <ul className="divide-y divide-white/10 border-y border-white/10">
              {[
                "Private tea ceremony with a master",
                "Thoughtfully arranged cultural experiences",
                "High-end dining planning",
                "Traditional evenings (geisha, heritage)",
                "Fully managed transport and logistics",
              ].map((item, i) => (
                <FadeIn key={item} delay={i * 90}>
                  <li className="flex items-baseline gap-8 py-7 group" data-testid={`experience-item-${i}`}>
                    <span className="font-serif text-gold text-lg tabular-nums w-10">0{i + 1}</span>
                    <span className="font-serif text-2xl md:text-3xl text-bone/90 group-hover:text-bone transition-colors tracking-tight">
                      {item}
                    </span>
                  </li>
                </FadeIn>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="relative py-32 md:py-44 border-t border-white/5" data-testid="how-it-works-section">
        <div className="container-editorial">
          <FadeIn>
            <div className="overline">Section IV — How it works</div>
            <h2 className="mt-5 font-serif text-5xl md:text-6xl text-bone tracking-tight">
              Luxury should feel <span className="italic text-gold">effortless.</span>
            </h2>
          </FadeIn>

          <div className="mt-20 grid grid-cols-1 md:grid-cols-3 border-t border-l border-white/10">
            {[
              { n: "01", t: "Inquiry", d: "A quiet conversation — your pace, your interests, what you want to feel." },
              { n: "02", t: "Design", d: "A bespoke itinerary, written and rewritten until every hour is considered." },
              { n: "03", t: "Travel", d: "Arrive, be met, and let each day unfold as it was intended." },
            ].map((step, i) => (
              <FadeIn key={step.n} delay={i * 140}>
                <div className="relative p-10 md:p-14 border-r border-b border-white/10 min-h-[320px] flex flex-col" data-testid={`how-step-${step.n}`}>
                  <div className="font-serif text-gold text-7xl md:text-8xl leading-none">{step.n}</div>
                  <div className="mt-8 font-serif text-3xl text-bone tracking-tight">{step.t}</div>
                  <p className="mt-5 text-bone/60 font-light leading-relaxed max-w-xs">{step.d}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-40 md:py-56 border-t border-white/5" data-testid="final-cta-section">
        <div className="container-editorial text-center max-w-4xl mx-auto">
          <FadeIn>
            <div className="overline mb-8">Begin</div>
            <h2 className="font-serif text-5xl md:text-7xl lg:text-8xl text-bone tracking-tight leading-[1.02]">
              Plan Your <span className="italic text-gold">Journey</span>
            </h2>
          </FadeIn>
          <FadeIn delay={180}>
            <p className="mt-10 text-bone/60 font-light max-w-xl mx-auto">
              We take on a limited number of clients each month.
            </p>
          </FadeIn>
          <FadeIn delay={320}>
            <div className="mt-14">
              <Link
                to="/plan"
                className="inline-flex items-center gap-4 bg-gold text-ink px-10 py-5 text-[12px] tracking-[0.28em] uppercase font-medium hover:bg-gold-light transition-colors duration-500"
                data-testid="final-cta-btn"
              >
                Plan Your Journey
                <span className="w-8 h-px bg-ink/60" />
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </main>
  );
};

export default Home;
